/**
 * Player preferences — accessibility first.
 *
 * Applied as data attributes and custom properties on <html> so CSS can react
 * without React re-rendering the tree, and so Phaser can read the same values.
 *
 * Two rules here are safety, not taste:
 *  1. `prefers-reduced-motion: reduce` clamps effects to at most 'reduced'.
 *     The OS setting always wins; a stored preference cannot override it upward.
 *  2. 'full' effects (the only level with flashing) are unreachable until the
 *     player has actually seen the photosensitivity notice. Default is 'reduced'.
 */

export type Effects = 'full' | 'reduced' | 'off';
export type TextSpeed = 'slow' | 'normal' | 'fast' | 'instant';

export interface Prefs {
  effects: Effects;
  textSpeed: TextSpeed;
  /** Multiplier on dialogue type size. */
  textScale: number;
  /** Set once the photosensitivity notice has been acknowledged. */
  seenWarning: boolean;
  /** Master volume, 0-100. The settings screen shows it as a percentage. */
  volume: number;
  /** Background music on. Separate from volume so muting music keeps sfx. */
  music: boolean;
  /*
   * Notification switches. The board gives all three a real toggle, so they
   * are real preferences and persist — there is no notification service behind
   * them yet, and these are what it will read when there is.
   */
  notifyNovels: boolean;
  notifyOffers: boolean;
  notifyCurrency: boolean;
  /** Softens explicit content. No content is tagged for it yet. */
  censor: boolean;
}

const KEY = 'hon.prefs';

export const DEFAULTS: Prefs = {
  // Deliberately NOT 'full'. Flashing is opt-in, never opt-out.
  effects: 'reduced',
  textSpeed: 'normal',
  textScale: 1,
  seenWarning: false,
  volume: 50,
  music: true,
  notifyNovels: true,
  notifyOffers: false,
  notifyCurrency: true,
  censor: false,
};

/** Characters per second, by setting. `instant` skips the typewriter entirely. */
export const CPS: Record<TextSpeed, number> = {
  slow: 14,
  normal: 34,
  fast: 60,
  instant: Infinity,
};

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function load(): Prefs {
  let stored: Partial<Prefs> = {};
  try {
    stored = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Prefs>;
  } catch {
    // Corrupt prefs must never block boot.
  }
  return clamp({ ...DEFAULTS, ...stored });
}

export function save(p: Prefs): Prefs {
  const next = clamp(p);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private mode / quota. Preferences degrade to session-only.
  }
  apply(next);
  return next;
}

/** Enforce the two safety rules above. */
function clamp(p: Prefs): Prefs {
  let effects = p.effects;
  if (effects === 'full' && (prefersReducedMotion() || !p.seenWarning)) effects = 'reduced';
  return {
    ...p,
    effects,
    textScale: Math.min(1.5, Math.max(1, p.textScale)),
    volume: Math.min(100, Math.max(0, Math.round(p.volume))),
  };
}

export function apply(p: Prefs): void {
  const root = document.documentElement;
  root.dataset['effects'] = p.effects;
  root.style.setProperty('--text-scale', String(p.textScale));
  // Device pixel ratio drives the scanline tile; see crt.ts.
  root.style.setProperty('--dpr', String(window.devicePixelRatio || 1));
  // Phaser reads this rather than being told, so audio and UI cannot drift apart.
  root.style.setProperty('--volume', String(p.volume / 100));
}
